import { useMemo, useState } from 'react';
import type {
  CatalystPortalContactMatch,
  CatalystPortalContactSyncResult,
  UpdateCatalystPortalScheduleInput,
} from '@catalyst-psa/shared';
import {
  CATALYST_PORTAL_DEFAULT_INTERVAL_MINUTES,
  CATALYST_PORTAL_MAX_INTERVAL_MINUTES,
  CATALYST_PORTAL_MIN_INTERVAL_MINUTES,
  portalRoleFlagLabel,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { TextField } from '../../components/ui/Field.js';
import {
  useCatalystPortalContactCandidates,
  useCatalystPortalContactSchedule,
  useRunCatalystPortalContactSync,
  useUpdateCatalystPortalContactSchedule,
} from './api.js';

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function errorMessage(error: unknown, fallback: string): string | null {
  if (!error) return null;
  return error instanceof ApiError ? error.message : fallback;
}

function matchedByLabel(match: CatalystPortalContactMatch): string {
  if (match.matchedBy === 'employee_id') return 'NetSuite Employee ID';
  if (match.matchedBy === 'email') return 'Email';
  return '—';
}

/** The role flags the portal reports, as "Label: on/off" chips. */
function FlagChips({ flags }: { flags: Record<string, boolean> }): JSX.Element {
  const keys = Object.keys(flags).sort();
  if (keys.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {keys.map((key) => (
        <Badge key={key} tone={flags[key] ? 'green' : 'gray'}>
          {portalRoleFlagLabel(key)}
        </Badge>
      ))}
    </div>
  );
}

function ResultSummary({ result }: { result: CatalystPortalContactSyncResult }): JSX.Element {
  const tone = result.outcome === 'ok' ? 'green' : result.outcome === 'partial' ? 'amber' : 'red';
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={tone}>{result.outcome}</Badge>
        {result.dryRun ? <Badge tone="blue">Preview only</Badge> : null}
        {result.scoped ? <Badge tone="gray">Selected contacts</Badge> : null}
        <span className="text-slate-600">
          {result.scanned} scanned · {result.matched} matched · {result.unmatched} not in the portal
          · {result.unchanged} already current ·{' '}
          <strong>
            {result.updated} {result.dryRun ? 'would change' : 'updated'}
          </strong>
          {result.employeeIdsBackfilled > 0
            ? ` · ${result.employeeIdsBackfilled} employee ID(s) back-filled`
            : ''}
        </span>
      </div>
      {result.message ? <p className="text-sm text-amber-600">{result.message}</p> : null}
      {result.changes.length ? (
        <ul className="max-h-48 space-y-1 overflow-auto text-xs text-slate-600">
          {result.changes.map((change) => (
            <li key={change.contactId}>
              <span className="font-medium text-slate-900">{change.contactName}</span>{' '}
              <span className="text-slate-400">({change.customerName})</span> — matched on{' '}
              {matchedByLabel(change)}
              {change.backfillEmployeeId
                ? `, employee ID → ${change.backfillEmployeeId}`
                : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Schedules and runs the portal user → PSA contact mirror, and lets an admin test the match
 * against hand-picked individual contacts before running it for everyone.
 */
export function ContactSyncCard(): JSX.Element {
  const scheduleQuery = useCatalystPortalContactSchedule();
  const updateSchedule = useUpdateCatalystPortalContactSchedule();
  const runSync = useRunCatalystPortalContactSync();

  const schedule = scheduleQuery.data;
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [interval, setInterval] = useState<string | null>(null);
  const [scheduleSaved, setScheduleSaved] = useState(false);

  const [showTest, setShowTest] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const candidatesQuery = useCatalystPortalContactCandidates(search, showTest);

  const effectiveEnabled = enabled ?? schedule?.enabled ?? false;
  const effectiveInterval =
    interval ?? String(schedule?.intervalMinutes ?? CATALYST_PORTAL_DEFAULT_INTERVAL_MINUTES);

  const candidates = candidatesQuery.data ?? [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggle = (contactId: string, checked: boolean): void => {
    setSelected((current) =>
      checked ? [...current, contactId] : current.filter((id) => id !== contactId),
    );
  };

  const handleSaveSchedule = (): void => {
    setScheduleSaved(false);
    const parsed = Number(effectiveInterval);
    const input: UpdateCatalystPortalScheduleInput = {
      enabled: effectiveEnabled,
      intervalMinutes: Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null,
    };
    updateSchedule.mutate(input, {
      onSuccess: () => {
        setScheduleSaved(true);
        setEnabled(null);
        setInterval(null);
      },
    });
  };

  const scheduleError = errorMessage(updateSchedule.error, 'Could not save the schedule.');
  const runError = errorMessage(runSync.error, 'The sync failed.');
  const result = runSync.data;

  return (
    <Card>
      <div className="space-y-5">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Portal users → contacts</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Reads <span className="font-mono">/v1/portal/users</span> and mirrors each portal
            account onto the matching PSA contact. Contacts with a{' '}
            <strong>NetSuite Employee ID</strong> match on that id; the rest match on email
            address, and their employee ID is back-filled for next time. The portal data is
            read-only in PSA — the contact&apos;s own access fields are never overwritten.
          </p>
        </div>

        <DataState
          isLoading={scheduleQuery.isLoading}
          isError={scheduleQuery.isError}
          error={scheduleQuery.error}
          onRetry={() => void scheduleQuery.refetch()}
          isEmpty={false}
        >
          {schedule ? (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Last run</div>
                  <div className="text-slate-900">{formatDateTime(schedule.lastRunAt)}</div>
                </div>
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Next run</div>
                  <div className="text-slate-900">{formatDateTime(schedule.nextRunAt)}</div>
                </div>
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Last outcome</div>
                  <div>
                    {schedule.lastOutcome ? (
                      <Badge
                        tone={
                          schedule.lastOutcome === 'ok'
                            ? 'green'
                            : schedule.lastOutcome === 'partial'
                              ? 'amber'
                              : 'red'
                        }
                      >
                        {schedule.lastOutcome}
                      </Badge>
                    ) : (
                      <span className="text-slate-500">Never run</span>
                    )}
                  </div>
                </div>
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Last totals</div>
                  <div className="text-slate-900">
                    {schedule.lastMatched} matched · {schedule.lastUpdated} updated
                  </div>
                </div>
              </div>

              {schedule.lastMessage ? (
                <p className="text-sm text-amber-600">{schedule.lastMessage}</p>
              ) : null}

              <div className="flex flex-wrap items-end gap-4">
                <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={effectiveEnabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                  Run automatically
                </label>
                <div className="w-48">
                  <TextField
                    label="Every (minutes)"
                    type="number"
                    min={CATALYST_PORTAL_MIN_INTERVAL_MINUTES}
                    max={CATALYST_PORTAL_MAX_INTERVAL_MINUTES}
                    step={15}
                    value={effectiveInterval}
                    onChange={(e) => setInterval(e.target.value)}
                    disabled={!effectiveEnabled}
                  />
                </div>
                <Button
                  variant="secondary"
                  onClick={handleSaveSchedule}
                  isLoading={updateSchedule.isPending}
                >
                  Save schedule
                </Button>
                <Button
                  onClick={() => runSync.mutate({})}
                  isLoading={runSync.isPending && !runSync.variables?.contactIds}
                >
                  Run now
                </Button>
                <Button variant="ghost" onClick={() => setShowTest((open) => !open)}>
                  {showTest ? 'Hide test contacts' : 'Test individual contacts'}
                </Button>
              </div>

              <p className="text-xs text-slate-500">
                Scheduled runs are checked every 15 minutes, so the interval is honoured to the
                nearest quarter hour. The minimum interval is{' '}
                {CATALYST_PORTAL_MIN_INTERVAL_MINUTES} minutes.
              </p>

              {scheduleError ? <p className="text-sm text-rose-600">{scheduleError}</p> : null}
              {scheduleSaved ? <p className="text-sm text-emerald-600">Schedule saved.</p> : null}
              {runError ? <p className="text-sm text-rose-600">{runError}</p> : null}
              {result ? <ResultSummary result={result} /> : null}
            </div>
          ) : null}
        </DataState>

        {showTest ? (
          <div className="space-y-3 border-t border-slate-200 pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-72">
                <TextField
                  label="Find contacts"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name, email, customer or employee ID"
                />
              </div>
              <Button
                variant="secondary"
                onClick={() => runSync.mutate({ contactIds: selected, dryRun: true })}
                disabled={selected.length === 0}
                isLoading={runSync.isPending && runSync.variables?.dryRun === true}
              >
                Preview selected
              </Button>
              <Button
                onClick={() => runSync.mutate({ contactIds: selected, dryRun: false })}
                disabled={selected.length === 0}
                isLoading={
                  runSync.isPending &&
                  Boolean(runSync.variables?.contactIds) &&
                  runSync.variables?.dryRun !== true
                }
              >
                Sync selected ({selected.length})
              </Button>
            </div>

            <DataState
              isLoading={candidatesQuery.isLoading}
              isError={candidatesQuery.isError}
              error={candidatesQuery.error}
              onRetry={() => void candidatesQuery.refetch()}
              isEmpty={candidates.length === 0}
              emptyTitle="No matching contacts"
              emptyDescription="No contacts with an email address or employee ID matched this search."
            >
              <div className="max-h-96 overflow-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="w-10 px-3 py-2" />
                      <th className="px-3 py-2">Contact</th>
                      <th className="px-3 py-2">Customer</th>
                      <th className="px-3 py-2">Employee ID</th>
                      <th className="px-3 py-2">Matched on</th>
                      <th className="px-3 py-2">Portal status</th>
                      <th className="px-3 py-2">Role flags</th>
                      <th className="px-3 py-2">Result</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {candidates.map((match) => (
                      <tr key={match.contactId} className="align-top">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedSet.has(match.contactId)}
                            onChange={(e) => toggle(match.contactId, e.target.checked)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900">{match.contactName}</div>
                          <div className="text-xs text-slate-500">{match.contactEmail ?? '—'}</div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{match.customerName}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">
                          {match.netsuiteEmployeeId ?? (
                            <span className="text-slate-400">not set</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{matchedByLabel(match)}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {match.portal?.portalStatus ?? '—'}
                        </td>
                        <td className="px-3 py-2">
                          <FlagChips flags={match.portal?.portalRoleFlags ?? {}} />
                        </td>
                        <td className="px-3 py-2">
                          {!match.matched ? (
                            <Badge tone="gray">Not in the portal</Badge>
                          ) : match.changed ? (
                            <Badge tone="amber">
                              {match.backfillEmployeeId ? 'Will back-fill ID' : 'Will update'}
                            </Badge>
                          ) : (
                            <Badge tone="green">Current</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DataState>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
